import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {Company} from 'src/app/views/wrapper/admin/company/company';
import {environment} from 'src/environments/environment';
import {IResponse} from '../service';
import {CreatePayrollRequest} from './company';
import {tap} from 'rxjs/operators';
import {AuthService} from '../auth/auth.service';
import { CalculateWorkingHourRequest, GetAttendanceEmployeeRequest, UpdateClockInRequest, UpdatePayCutRequest, UpdateWorkingHourRequest } from 'src/app/views/wrapper/payroll/company/setting/components/attendance-form/attendance';
import { Holiday, Payment } from 'src/app/views/wrapper/payroll/company/setting/settings';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  companySubject = new BehaviorSubject<any>(null);
  private isLoading = false;
  currentCompany: any;
  resetGetCompanyProfile = false;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
  }

  createCompany(body: Company): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies`, {
      ...body
    }).pipe(
      tap(() => {
        this.authService.resetGetAuthProfile = true;
        this.authService.getCurrentUser();
      })
    );
  }

  updateCompany(body: Company): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${body.companyId}`, {
      ...body
    }).pipe(
      tap(() => {
        this.resetGetCompanyProfile = true;
        this.getCompanyDetail(body.companyId);
      })
    );
  }

  getCompaniesGroup(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/group`);
  }

  updateCompanyLogo(id: string, file: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.put(`${environment.apiBaseUrl}/companies/${id}/logo`, formData).pipe(
      tap(() => {
        this.resetGetCompanyProfile = true;
        this.getCompanyDetail(id).subscribe(() => {
          this.authService.resetGetAuthProfile = true;
          this.authService.getCurrentUser();
        });
      })
    );
  }

  deleteCompany(id: string): Observable<any> {
    return this.http.delete(`${environment.apiBaseUrl}/companies/${id}`).pipe(
      tap(() => {
        this.authService.resetGetAuthProfile = true;
        this.authService.getCurrentUser();
      })
    );
  }

  updateCompanyGroup(id: string, group: string): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${id}/group`, {
      group_id: group
    });
  }

  get companyObs(): Observable<any> {
    return this.companySubject.asObservable();
  }

  getCompany(id: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${id}`);
  }

  getCompanyDetail(id: string): BehaviorSubject<any> {
    if (this.currentCompany && !this.resetGetCompanyProfile) {
      this.companySubject.next(this.currentCompany);
    } else if (!this.isLoading || this.resetGetCompanyProfile) {
      this.isLoading = true;
      this.getCompany(id).subscribe((profile) => {
          this.currentCompany = {...profile, time: new Date().getTime()};
          this.isLoading = false;
          this.companySubject.next(this.currentCompany);
        },
        (err) => {
          this.isLoading = false;
          this.companySubject.next(null);
          console.error(err);
        });
      return this.companySubject;
    }
  }

  updateSignature(companyId: string, file: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/signature`, formData);
  }

  updateSeal(companyId: string, file: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/seal`, formData);
  }

  deleteSignature(companyId: string): Observable<any> {
    return this.http.delete(`${environment.apiBaseUrl}/companies/${companyId}/signature`);
  }

  deleteSeal(companyId: string): Observable<any> {
    return this.http.delete(`${environment.apiBaseUrl}/companies/${companyId}/seal`);
  }

  getPayrollMonthlyList(companyId: string, year: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/list/${year}`);
  }

  getPayrollYear(companyId: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll`);
  }

  // generateNewPayroll(body: CreatePayrollRequest): Observable<IResponse> {
  //   return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${body.companyId}/payroll/create`, body);
  // }
  generateNewPayroll(body: CreatePayrollRequest): Observable<IResponse> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${body.companyId}/payroll/init`, body);
  }

  generateAddMoreColumn(body): Observable<IResponse> {
    return this.http.patch<IResponse>(`${environment.apiBaseUrl}/companies/${body.companyId}/payroll/${body.payrollId}/columns`, body);
  }

  recalculateWithholdingTax(body): Observable<any> {
    return this.http.patch(`${environment.apiBaseUrl}/companies/${body.companyId}/payroll/${body.payrollId}/recalculate`, body);
  }

  confirmPayroll(body): Observable<IResponse> {
    return this.http.put<IResponse>(`${environment.apiBaseUrl}/companies/${body.companyId}/payroll/${body.payrollId}`, body);
  }

  createPayrollSummary(body, companyId): Observable<IResponse> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/summary`, body);
  }

  // getCompletedPayrollDetailByMonth(companyId: string, payrollId: string): Observable<IResponse> {
  //   return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}`);
  // }
  getCompletedPayrollDetailByMonth(companyId: string, payrollId: string, page: number, limit: number, filter: any, payrollGroupId: string = null): Observable<IResponse> {
    const body = {
      payroll_group_id: payrollGroupId,
      page_no: page,
      filter,
      limit
    };
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/page`, body);
  }

  getCompletedPayrollDetailByIds(companyId: string, payrollId: string, employeeIds: string[]): Observable<IResponse> {
    const body = {
      employee_ids: employeeIds
    };
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/idlist`, body);
  }

  saveDraftPayroll(body): Observable<IResponse> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${body.companyId}/payroll/${body.payrollId}`, body);
  }

  deleteEmployeeFromPayroll(body): Observable<IResponse> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${body.companyId}/payroll/${body.payrollId}/delete`, body);
  }

  checkDefaultColumns(companyId: string, params: any): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/payroll/defaultcolumns`, {params: params});
  }

  checkDefaultTimeClockColumns(companyId: string, params: any): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/payroll/timeclockcolumns`, {params: params});
  }

  checkDefaultRequestNoti(body): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${body.companyId}/payroll/notification`, body);
  }

  checkDefaultRequestSelfOnboard(body): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${body.companyId}/employees/filter`, body);
  }

  getDetailRequestNoti(body): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${body.companyId}/payroll/notification/detail`, body);
  }

  resetPayroll(companyId: string, payrollId: string): Observable<IResponse> {
    return this.http.put<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/reset`, {});
  }

  addMoreEmployeeToPayroll(body): Observable<any> {
    return this.http.patch(`${environment.apiBaseUrl}/companies/${body.companyId}/payroll/${body.payrollId}/employees`, body);
  }

  getEditPayroll(body): Observable<IResponse> {
    return this.http.put<IResponse>(`${environment.apiBaseUrl}/companies/${body.companyId}/payroll/${body.payrollId}/edit`, body);
  }

  generateYearlySummary(companyId: string, year: number): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/payroll/annual/${year}`, {});
  }

  getAnnualReportFile(companyId: string, payrollId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/payroll/annual/${payrollId}/annualexcelreport`);
  }

  editCompletedPayroll(companyId: string, payrollId: string): Observable<IResponse> {
    return this.http.put<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/edit`, {});
  }

  getEmployeeNoMail(companyId: string, payrollId: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/payrollnoemail`);
  }

  requestPaySlipEmail(companyId: string, payrollId: string, body: any): Observable<IResponse> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/paysliprequestemail`, body);
  }

  editAnnualFile(companyId: string, payrollId: string): Observable<IResponse> {
    return this.http.put<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/annual/${payrollId}/edit`, {});
  }

  getEfiling(companyId: string, payrollId: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/taxfiling`);
  }

  previewEfiling(companyId: string, payrollId: string): Observable<IResponse> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/taxfiling/preview`, {});
  }

  submitEfiling(companyId: string, payrollId: string, body: any): Observable<IResponse> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/taxfiling`, body);
  }

  getQrPayment(companyId: string, payrollId: string, taxfilingId: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/taxfiling/${taxfilingId}/qrpayment`);
  }

  getPayinSlip(companyId: string, payrollId: string, taxfilingId: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/taxfiling/${taxfilingId}/payinslip`);
  }

  getTaxformPnd1(companyId: string, payrollId: string, taxfilingId: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/taxfiling/${taxfilingId}/taxformpnd1`);
  }

  getReceipt(companyId: string, payrollId: string, taxfilingId: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/taxfiling/${taxfilingId}/receipt`);
  }

  cancelEfiling(companyId: string, payrollId: string, taxfilingId: string): Observable<IResponse> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/taxfiling/${taxfilingId}/cancel`, {});
  }

  getPresignFile(body: any): Observable<IResponse> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${body.companyId}/notification/${body.notificationId}/file`, body);
  }

  getPayrollFilters(companyId: string, payrollId: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/${payrollId}/filterlist`);
  }

  savePayrollAutoRecal(body): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${body.companyId}/payroll/${body.payrollId}/saveautorecal`, body);
  }

  checkYearlyRequiredInfo(companyId: string, year: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/annual/${year}/requiredinfo`);
  }

  getYearlySummary(companyId: string, payrollId: string, page: number, limit: number, filter: any): Observable<any> {
    const body = {
      page_no: page,
      filter,
      limit
    };
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/payroll/annual/${payrollId}/page`, body);
  }

  getYearlyPayrollFilters(companyId: string, payrollId: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/payroll/annual/${payrollId}/filterlist`);
  }

  calculateCompanyWorkingHour(companyId: string, body: CalculateWorkingHourRequest): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/workinghours/calculate`, body);
  }

  getCompanyWorkingHour(companyId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/workinghours`);
  }
  getCompanyClockIn(companyId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/clockin`);
  }
  getCompanyPayCut(companyId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/paycut`);
  }

  updateCompanyWorkingHour(companyId: string, body: UpdateWorkingHourRequest): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/workinghours`, body);
  }
  updateCompanyClockIn(companyId: string, body: UpdateClockInRequest): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/clockin`, body);
  }
  updateCompanyPayCut(companyId: string, body: UpdatePayCutRequest): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/paycut`, body);
  }

  deleteCompanyWorkingHour(companyId: string, id: string): Observable<any> {
    return this.http.delete(`${environment.apiBaseUrl}/companies/${companyId}/workinghours/${id}`);
  }
  deleteCompanyClockIn(companyId: string, id: string): Observable<any> {
    return this.http.delete(`${environment.apiBaseUrl}/companies/${companyId}/clockin/${id}`);
  }
  deleteCompanyPayCut(companyId: string, id: string): Observable<any> {
    return this.http.delete(`${environment.apiBaseUrl}/companies/${companyId}/paycut/${id}`);
  }

  getCompanyWorkingHourEmployee(companyId: string, body: GetAttendanceEmployeeRequest, isSelf: boolean): Observable<any> {
    let path = (isSelf) ? 'employee' : 'otheremployee'
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/workinghours/${path}`, body);
  }
  getCompanyClockInEmployee(companyId: string, body: GetAttendanceEmployeeRequest, isSelf: boolean): Observable<any> {
    let path = (isSelf) ? 'employee' : 'otheremployee'
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/clockin/${path}`, body);
  }
  getCompanyPayCutEmployee(companyId: string, body: GetAttendanceEmployeeRequest, isSelf: boolean): Observable<any> {
    let path = (isSelf) ? 'employee' : 'otheremployee'
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/paycut/${path}`, body);
  }

  getCompanyClockReportYearList(companyId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/timeclock/`);
  }
  getCompanyClockReportMonthList(companyId: string, year: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/timeclock/list/${year}`);
  }
  getCompanyClockReportEmployee(companyId: string, body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/timeclock/`, body);
  }
  getTimeClockImageURL(companyId: string, timeClockId: string, clockType: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/timeclock/${timeClockId}/image/${clockType}`);
  }

  getCompanyDepartments(companyId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/departments`);
  }

  getCompanyHoliday(companyId: string, year: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/holiday/${year}`);
  }
  updateCompanyHoliday(companyId: string, year: string, holidays: Holiday[]): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/holiday/${year}`, {holidays});
  }

  getJobCompanyInfo(companyId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/info`);
  }
  updateJobCompanyInfo(companyId: string, body: any): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/info`, body);
  }
  
  getJobApplication(companyId: string, params: any): Observable<any> {
    return this.http.get(`${environment.jobBaseUrl}/${companyId}/job-applications`, {params: params});
  }
  getCountJobApplication(companyId: string): Observable<any> {
    return this.http.get(`${environment.jobBaseUrl}/${companyId}/job-applications/count-by-job-post`);
  }

  rejectJobApplication(companyId: string, jobAppId: string): Observable<any> {
    return this.http.post(`${environment.jobBaseUrl}/${companyId}/job-applications/${jobAppId}/reject`, null);
  }
  offerJobApplication(companyId: string, jobAppId: string): Observable<any> {
    return this.http.post(`${environment.jobBaseUrl}/${companyId}/job-applications/${jobAppId}/offer`, null);
  }
  createJobApplicationSelfOnboard(companyId: string, jobAppId: string, body: any): Observable<any> {
    return this.http.post(`${environment.jobBaseUrl}/${companyId}/job-applications/${jobAppId}/self-onboarding-employee`, body);
  }
  deleteJobApplication(companyId: string, jobAppId: string): Observable<any> {
    return this.http.delete(`${environment.jobBaseUrl}/${companyId}/job-applications/${jobAppId}`);
  }

  updateCompanyPayment(companyId: string, payment: Payment): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/payment`, payment);
  }

  getCompanyPayment(companyId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/payment`);
  }
}
